import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { 
  ChevronRight, 
  Clock, 
  Calendar, 
  ChevronDown, 
  ChevronUp,
  CheckCircle2,
  AlertTriangle,
  Lightbulb,
  ArrowRight,
  Shield,
  Target,
  HelpCircle,
  Award,
  FileCheck,
  Scale,
  Building2,
  RefreshCw,
  ClipboardCheck,
  Gauge
} from 'lucide-react';

const EtalonnageCapteurs = () => {
  const [activeSection, setActiveSection] = useState('');
  const [openFaq, setOpenFaq] = useState(null);

  // Table of contents
  const sections = [
    { id: 'section-1', title: "Pourquoi l'étalonnage est critique" },
    { id: 'section-2', title: "Étalonnage vs vérification" },
    { id: 'section-3', title: "La traçabilité métrologique" },
    { id: 'section-4', title: "Les obligations réglementaires" },
    { id: 'section-5', title: "À quelle fréquence étalonner ?" },
    { id: 'section-6', title: "Le certificat d'étalonnage" },
    { id: 'section-7', title: "Bien choisir son prestataire" },
  ];

  const faqs = [
    {
      question: "L'étalonnage est-il obligatoire pour une entreprise de forage ?",
      answer: "Pas de façon universelle, mais il le devient dès que l'entreprise est soumise à un référentiel qualité (ISO 9001, ISO 17025), que la mesure entre dans une validation contractuelle, ou que le cahier des charges du marché l'impose. Dans les faits, c'est devenu un standard sur les chantiers exigeants."
    },
    {
      question: "Quelle est la différence entre étalonnage et vérification ?",
      answer: "L'étalonnage mesure et documente l'écart réel de l'instrument par rapport à un étalon. La vérification confirme seulement que l'instrument reste dans une tolérance acceptable. L'un quantifie, l'autre valide."
    },
    {
      question: "À quelle fréquence faut-il étalonner ses capteurs ?",
      answer: "Le plus souvent annuellement, mais un usage intensif peut justifier une périodicité semestrielle ou trimestrielle. Un capteur ayant subi un choc doit être réétalonné avant réutilisation."
    },
    {
      question: "Que risque-t-on à travailler avec des capteurs non étalonnés ?",
      answer: "Une non-conformité en audit, des litiges sur des mesures erronées, et surtout l'invalidité des données comme preuve d'exécution — ce qui peut être lourd de conséquences en cas de désordre sur l'ouvrage."
    },
    {
      question: "Peut-on étalonner en interne ?",
      answer: "C'est possible pour des organismes disposant des étalons, des conditions et des compétences requises (documentées selon l'ISO/IEC 17025). Mais la plupart des entreprises font appel à un prestataire accrédité, ce qui évite les questions lors des évaluations et garantit la traçabilité."
    }
  ];

  // Track scroll for active section
  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY + 150;
      for (const section of sections) {
        const element = document.getElementById(section.id);
        if (element) {
          const { offsetTop, offsetHeight } = element;
          if (scrollPosition >= offsetTop && scrollPosition < offsetTop + offsetHeight) {
            setActiveSection(section.id);
            break;
          }
        }
      }
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      const offset = 100;
      const elementPosition = element.getBoundingClientRect().top + window.pageYOffset;
      window.scrollTo({ top: elementPosition - offset, behavior: 'smooth' });
    }
  };

  // Schema.org JSON-LD
  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": "Étalonnage des capteurs de forage : obligations, normes et bonnes pratiques",
    "description": "Étalonnage des capteurs de forage et d'instrumentation géotechnique : obligations, norme ISO 17025, traçabilité métrologique, fréquence et certificat. Le guide complet.",
    "image": "https://geots.consulting/og-images/guide-etalonnage.jpg",
    "author": {
      "@type": "Organization",
      "name": "GEOTS Consulting"
    },
    "publisher": {
      "@type": "Organization",
      "name": "GEOTS Consulting",
      "logo": {
        "@type": "ImageObject",
        "url": "https://geots.consulting/logo.png"
      }
    },
    "datePublished": "2026-03-15",
    "dateModified": "2026-03-15"
  };

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqs.map(faq => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.answer
      }
    }))
  };

  return (
    <>
      <Helmet>
        <title>Étalonnage des capteurs de forage : obligations, normes et bonnes pratiques | Guide</title>
        <meta name="description" content="Étalonnage des capteurs de forage et d'instrumentation géotechnique : obligations, norme ISO 17025, traçabilité métrologique, fréquence et certificat. Le guide complet." />
        <link rel="canonical" href="https://geots.consulting/guides/etalonnage-capteurs-forage" />
        <meta property="og:title" content="Étalonnage des capteurs de forage : obligations, normes et bonnes pratiques" />
        <meta property="og:description" content="Étalonnage des capteurs de forage et d'instrumentation géotechnique : obligations, norme ISO 17025, traçabilité métrologique, fréquence et certificat." />
        <meta property="og:type" content="article" />
        <meta property="og:url" content="https://geots.consulting/guides/etalonnage-capteurs-forage" />
        <meta name="twitter:card" content="summary_large_image" />
        <script type="application/ld+json">{JSON.stringify(articleSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
      </Helmet>

      <div className="min-h-screen bg-gray-50">
        {/* Breadcrumb */}
        <div className="bg-white border-b">
          <div className="container mx-auto px-4 py-3">
            <nav className="flex items-center gap-2 text-sm text-gray-600">
              <Link to="/" className="hover:text-[#635BFF]">Accueil</Link>
              <ChevronRight className="w-4 h-4" />
              <Link to="/guides" className="hover:text-[#635BFF]">Guides</Link>
              <ChevronRight className="w-4 h-4" />
              <span className="text-[#0A2540] font-medium">Étalonnage capteurs</span>
            </nav>
          </div>
        </div>

        {/* Hero Section */}
        <header className="bg-gradient-to-br from-[#0A2540] to-[#1a3a5c] text-white py-16">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl">
              <div className="flex items-center gap-4 mb-6 flex-wrap">
                <span className="bg-[#635BFF] text-white text-xs font-bold px-3 py-1 rounded-full">
                  GUIDE COMPLET
                </span>
                <span className="text-gray-300 text-sm flex items-center gap-1">
                  <Calendar className="w-4 h-4" />
                  Publié le 15 mars 2026
                </span>
                <span className="text-gray-300 text-sm flex items-center gap-1">
                  <Clock className="w-4 h-4" />
                  10 min de lecture
                </span>
              </div>
              <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-6 leading-tight">
                L'étalonnage des capteurs de forage :
                <span className="text-[#00D4FF]"> obligations, normes et bonnes pratiques</span>
              </h1>
              <p className="text-lg md:text-xl text-gray-300 mb-8">
                Un enregistreur de paramètres ne vaut que ce que valent ses capteurs. Une sonde de pression dérivée, 
                un capteur de couple mal réglé, et c'est toute la traçabilité d'un chantier de fondations qui devient 
                contestable. L'étalonnage n'est pas une formalité : c'est ce qui garantit que vos mesures ont une valeur 
                — technique, contractuelle et juridique.
              </p>
              <div className="flex flex-wrap gap-4">
                <Link 
                  to="/suppliers" 
                  className="bg-[#635BFF] hover:bg-[#5248e5] text-white px-6 py-3 rounded-lg font-semibold flex items-center gap-2 transition-colors"
                >
                  Trouver un prestataire
                  <ArrowRight className="w-5 h-5" />
                </Link>
                <Link 
                  to="/guides/enregistreur-parametres-forage" 
                  className="bg-white/10 hover:bg-white/20 text-white px-6 py-3 rounded-lg font-semibold flex items-center gap-2 transition-colors"
                >
                  Guide enregistreur de paramètres
                </Link>
              </div>
            </div>
          </div>
        </header>

        <div className="container mx-auto px-4 py-12">
          <div className="flex flex-col lg:flex-row gap-12">
            {/* Sidebar - Table of Contents */}
            <aside className="lg:w-72 flex-shrink-0">
              <div className="sticky top-24 bg-white rounded-xl shadow-sm border p-6">
                <h3 className="font-bold text-[#0A2540] mb-4 flex items-center gap-2">
                  <Target className="w-5 h-5 text-[#635BFF]" />
                  Sommaire
                </h3>
                <nav className="space-y-2">
                  {sections.map((section, index) => (
                    <button
                      key={section.id}
                      onClick={() => scrollToSection(section.id)}
                      className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors flex items-start gap-2 ${
                        activeSection === section.id
                          ? 'bg-[#635BFF]/10 text-[#635BFF] font-medium'
                          : 'text-gray-600 hover:bg-gray-100'
                      }`}
                    >
                      <span className="text-xs font-bold text-gray-400 mt-0.5">{index + 1}.</span>
                      {section.title}
                    </button>
                  ))}
                </nav>

                {/* Quick links */}
                <div className="mt-6 pt-6 border-t">
                  <p className="text-xs text-gray-500 uppercase font-bold mb-3">Voir aussi</p>
                  <div className="space-y-2">
                    <Link to="/guides/enregistreur-parametres-forage" className="block text-sm text-[#635BFF] hover:underline">
                      Enregistreur de paramètres
                    </Link>
                    <Link to="/guides/materiel-de-forage" className="block text-sm text-[#635BFF] hover:underline">
                      Matériel de forage
                    </Link>
                    <Link to="/guides/choisir-pressiometre-manuel-automatique" className="block text-sm text-[#635BFF] hover:underline">
                      Choisir son pressiomètre
                    </Link>
                  </div>
                </div>
              </div>
            </aside>

            {/* Main Content */}
            <main className="flex-1 max-w-3xl">
              <article className="prose prose-lg max-w-none">
                
                {/* Section 1 */}
                <section id="section-1" className="mb-16">
                  <h2 className="text-3xl font-bold text-[#0A2540] mb-6 flex items-center gap-3">
                    <span className="bg-[#635BFF] text-white w-10 h-10 rounded-full flex items-center justify-center text-lg">1</span>
                    Pourquoi l'étalonnage des capteurs est un enjeu critique
                  </h2>
                  
                  <p className="text-gray-700 mb-6">
                    Sur un chantier de forage ou de fondations spéciales, l'instrumentation 
                    (<Link to="/guides/enregistreur-parametres-forage" className="text-[#635BFF] hover:underline">enregistreur de paramètres</Link>, 
                    capteurs de pression, de couple, de profondeur, d'inclinaison, sondes d'injection) 
                    produit les <strong>données qui prouvent la bonne exécution de l'ouvrage</strong>. 
                    Comme l'ouvrage fini est enterré, ces mesures sont souvent le seul témoin fiable de ce qui s'est passé.
                  </p>

                  <p className="text-gray-700 mb-6">
                    Or un capteur, avec le temps et l'usage, <strong>dérive</strong>. Il se met à mesurer faux, 
                    sans que rien ne le signale visuellement. Un enregistrement qui semble parfait peut reposer 
                    sur une sonde décalée de plusieurs pourcents.
                  </p>

                  <h3 className="text-xl font-bold text-[#0A2540] mb-4">Les conséquences d'un capteur mal étalonné</h3>

                  <div className="space-y-4 mb-8">
                    {[
                      { 
                        icon: AlertTriangle, 
                        color: 'red',
                        title: "Un dimensionnement faussé", 
                        desc: "Des paramètres d'injection ou de forage erronés remettent en cause la validation de l'ouvrage." 
                      },
                      { 
                        icon: ClipboardCheck, 
                        color: 'orange',
                        title: "Un contrôle qualité invalidé", 
                        desc: "Lors d'un audit, des mesures non traçables ne valent rien." 
                      },
                      { 
                        icon: Scale, 
                        color: 'purple',
                        title: "Un risque juridique", 
                        desc: "En cas de litige ou de désordre, des données issues d'un capteur non étalonné ne constituent pas une preuve recevable." 
                      },
                    ].map((item, i) => (
                      <div key={i} className={`bg-${item.color}-50 border border-${item.color}-200 rounded-xl p-4 flex items-start gap-3`}>
                        <item.icon className={`w-6 h-6 text-${item.color}-500 flex-shrink-0 mt-0.5`} />
                        <div>
                          <h4 className="font-bold text-gray-800">{item.title}</h4>
                          <p className="text-gray-700 text-sm">{item.desc}</p>
                        </div>
                      </div>
                    ))}
                  </div>

                  <div className="bg-[#635BFF]/5 border border-[#635BFF]/20 rounded-xl p-6">
                    <p className="text-[#0A2540] font-medium">
                      L'étalonnage, c'est ce qui transforme une mesure « affichée » en mesure <strong>fiable et opposable</strong>.
                    </p>
                  </div>
                </section>

                {/* Section 2 */}
                <section id="section-2" className="mb-16">
                  <h2 className="text-3xl font-bold text-[#0A2540] mb-6 flex items-center gap-3">
                    <span className="bg-[#635BFF] text-white w-10 h-10 rounded-full flex items-center justify-center text-lg">2</span>
                    Étalonnage, vérification : ne pas confondre
                  </h2>

                  <p className="text-gray-700 mb-6">
                    Deux termes souvent employés à tort comme synonymes :
                  </p>

                  <div className="grid md:grid-cols-2 gap-6 mb-8">
                    <div className="bg-white rounded-xl border p-6">
                      <div className="bg-blue-100 w-12 h-12 rounded-xl flex items-center justify-center mb-4">
                        <Gauge className="w-6 h-6 text-blue-600" />
                      </div>
                      <h3 className="text-xl font-bold text-[#0A2540] mb-2">L'étalonnage</h3>
                      <p className="text-gray-700">
                        Mesure et <strong>documente l'écart réel</strong> de l'instrument par rapport à un étalon de référence. 
                        Il quantifie la dérive, <strong>sans juger</strong>.
                      </p>
                    </div>
                    <div className="bg-white rounded-xl border p-6">
                      <div className="bg-green-100 w-12 h-12 rounded-xl flex items-center justify-center mb-4">
                        <CheckCircle2 className="w-6 h-6 text-green-600" />
                      </div>
                      <h3 className="text-xl font-bold text-[#0A2540] mb-2">La vérification</h3>
                      <p className="text-gray-700">
                        Confirme simplement que l'instrument reste <strong>dans une tolérance acceptable</strong> pour un usage donné. 
                        Elle statue <strong>conforme / non conforme</strong>.
                      </p>
                    </div>
                  </div>

                  <div className="bg-[#00D4FF]/10 border border-[#00D4FF]/30 rounded-lg p-4 flex items-start gap-2">
                    <Lightbulb className="w-5 h-5 text-[#00D4FF] flex-shrink-0 mt-0.5" />
                    <p className="text-sm text-[#0A2540]">
                      <strong>En pratique :</strong> on étalonne pour connaître l'écart, on vérifie pour valider l'aptitude à l'emploi. 
                      Les deux sont complémentaires, et un bon suivi métrologique combine les deux.
                    </p>
                  </div>
                </section>

                {/* Section 3 */}
                <section id="section-3" className="mb-16">
                  <h2 className="text-3xl font-bold text-[#0A2540] mb-6 flex items-center gap-3">
                    <span className="bg-[#635BFF] text-white w-10 h-10 rounded-full flex items-center justify-center text-lg">3</span>
                    La notion clé : la traçabilité métrologique
                  </h2>

                  <p className="text-gray-700 mb-6">
                    C'est le cœur du sujet. La <strong>traçabilité métrologique</strong> exige que tout résultat de mesure 
                    soit rattaché au <strong>Système International d'unités (SI)</strong> par une <strong>chaîne ininterrompue d'étalonnages</strong>.
                  </p>

                  <div className="bg-white rounded-xl border p-6 mb-6">
                    <h3 className="text-lg font-bold text-[#0A2540] mb-4">La pyramide de traçabilité</h3>
                    <div className="space-y-3">
                      {[
                        { level: "1", name: "Références SI internationales", desc: "BIPM (Bureau International des Poids et Mesures)" },
                        { level: "2", name: "Références nationales", desc: "LNE en France, PTB en Allemagne..." },
                        { level: "3", name: "Étalons de référence laboratoire", desc: "Laboratoires accrédités ISO 17025" },
                        { level: "4", name: "Vos capteurs de chantier", desc: "Étalonnés par un prestataire accrédité" },
                      ].map((item, i) => (
                        <div key={i} className="flex items-center gap-4">
                          <div className="bg-[#635BFF] text-white w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0">
                            {item.level}
                          </div>
                          <div className="flex-1">
                            <p className="font-semibold text-[#0A2540]">{item.name}</p>
                            <p className="text-sm text-gray-500">{item.desc}</p>
                          </div>
                          {i < 3 && <div className="text-gray-300">↓</div>}
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="bg-red-50 border border-red-200 rounded-xl p-4 flex items-start gap-3">
                    <AlertTriangle className="w-6 h-6 text-red-500 flex-shrink-0 mt-0.5" />
                    <div>
                      <h4 className="font-bold text-red-800">Attention</h4>
                      <p className="text-red-700 text-sm">
                        Une mesure sans cette chaîne de raccordement est, métrologiquement, une mesure <strong>orpheline</strong> : 
                        elle n'a aucune valeur de preuve.
                      </p>
                    </div>
                  </div>
                </section>

                {/* Section 4 */}
                <section id="section-4" className="mb-16">
                  <h2 className="text-3xl font-bold text-[#0A2540] mb-6 flex items-center gap-3">
                    <span className="bg-[#635BFF] text-white w-10 h-10 rounded-full flex items-center justify-center text-lg">4</span>
                    Les obligations : que dit la réglementation ?
                  </h2>

                  <p className="text-gray-700 mb-6">
                    Il n'existe pas d'obligation universelle unique — cela dépend du contexte, mais plusieurs cas rendent l'étalonnage <strong>obligatoire</strong> :
                  </p>

                  <div className="bg-white rounded-xl border overflow-hidden mb-8">
                    <table className="w-full">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="px-4 py-3 text-left text-sm font-semibold text-[#0A2540]">Contexte</th>
                          <th className="px-4 py-3 text-left text-sm font-semibold text-[#0A2540]">Étalonnage requis ?</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y">
                        {[
                          ["Référentiel qualité ISO 9001 ou ISO/IEC 17025", "Oui, obligatoire"],
                          ["Transaction commerciale ou validation contractuelle", "Oui, recommandé fortement"],
                          ["Cahier des charges du marché l'impose", "Oui, obligatoire"],
                          ["Chantier standard sans exigence spécifique", "Non obligatoire, mais recommandé"],
                        ].map(([context, required], i) => (
                          <tr key={i} className="hover:bg-gray-50">
                            <td className="px-4 py-3 text-gray-700">{context}</td>
                            <td className="px-4 py-3">
                              <span className={`inline-flex items-center gap-1 px-2 py-1 rounded text-sm font-medium ${
                                required.includes('Oui') ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'
                              }`}>
                                {required.includes('Oui') ? <CheckCircle2 className="w-4 h-4" /> : null}
                                {required}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  <div className="bg-[#635BFF]/5 border border-[#635BFF]/20 rounded-xl p-6 mb-6">
                    <div className="flex items-start gap-3">
                      <Award className="w-6 h-6 text-[#635BFF] flex-shrink-0 mt-0.5" />
                      <div>
                        <h4 className="font-bold text-[#0A2540] mb-2">La norme de référence : NF EN ISO/IEC 17025:2017</h4>
                        <p className="text-gray-700 text-sm">
                          C'est LA norme pour la compétence des laboratoires d'étalonnage. Un prestataire accrédité selon cette norme 
                          délivre des certificats reconnus, avec traçabilité au SI garantie.
                        </p>
                      </div>
                    </div>
                  </div>

                  <p className="text-gray-700">
                    Une entreprise qui néglige l'étalonnage <strong>s'expose</strong> : non-conformité lors d'un audit, 
                    litige commercial sur des mesures erronées, et remise en cause de la fiabilité de l'ensemble de ses résultats.
                  </p>
                </section>

                {/* Section 5 */}
                <section id="section-5" className="mb-16">
                  <h2 className="text-3xl font-bold text-[#0A2540] mb-6 flex items-center gap-3">
                    <span className="bg-[#635BFF] text-white w-10 h-10 rounded-full flex items-center justify-center text-lg">5</span>
                    À quelle fréquence étalonner ?
                  </h2>

                  <p className="text-gray-700 mb-6">
                    Il n'y a pas de règle unique : <strong>la fréquence dépend de la criticité et de l'usage</strong> de l'instrument.
                  </p>

                  <div className="grid md:grid-cols-3 gap-4 mb-8">
                    {[
                      { freq: "Annuelle", desc: "Référence la plus courante", icon: RefreshCw, color: "green" },
                      { freq: "Semestrielle", desc: "Usage intensif, chantiers enchaînés", icon: RefreshCw, color: "orange" },
                      { freq: "Immédiate", desc: "Après choc, surcharge ou réparation", icon: AlertTriangle, color: "red" },
                    ].map((item, i) => (
                      <div key={i} className={`bg-white rounded-xl border p-4 border-l-4 border-l-${item.color}-500`}>
                        <div className={`bg-${item.color}-100 w-10 h-10 rounded-lg flex items-center justify-center mb-3`}>
                          <item.icon className={`w-5 h-5 text-${item.color}-600`} />
                        </div>
                        <h4 className="font-bold text-[#0A2540]">{item.freq}</h4>
                        <p className="text-sm text-gray-600">{item.desc}</p>
                      </div>
                    ))}
                  </div>

                  <div className="bg-[#00D4FF]/10 border border-[#00D4FF]/30 rounded-lg p-4 flex items-start gap-2">
                    <Lightbulb className="w-5 h-5 text-[#00D4FF] flex-shrink-0 mt-0.5" />
                    <p className="text-sm text-[#0A2540]">
                      <strong>Bonne pratique :</strong> tenir un <strong>planning d'étalonnage</strong> pour tout le parc, 
                      avec les échéances par instrument, et vérifier la validité <strong>avant chaque chantier critique</strong>.
                    </p>
                  </div>
                </section>

                {/* Section 6 */}
                <section id="section-6" className="mb-16">
                  <h2 className="text-3xl font-bold text-[#0A2540] mb-6 flex items-center gap-3">
                    <span className="bg-[#635BFF] text-white w-10 h-10 rounded-full flex items-center justify-center text-lg">6</span>
                    Le certificat d'étalonnage : ce qu'il doit contenir
                  </h2>

                  <p className="text-gray-700 mb-6">
                    C'est le document qui fait foi. Un certificat d'étalonnage sérieux mentionne :
                  </p>

                  <div className="bg-white rounded-xl border p-6 mb-8">
                    <div className="grid md:grid-cols-2 gap-4">
                      {[
                        { icon: Gauge, title: "Valeurs mesurées", desc: "Écart constaté par rapport à l'étalon de référence" },
                        { icon: Target, title: "Incertitude de mesure", desc: "Marge d'erreur associée à la mesure" },
                        { icon: Shield, title: "Traçabilité métrologique", desc: "La chaîne de raccordement au SI" },
                        { icon: Calendar, title: "Date et identification", desc: "Date de l'étalonnage et ID de l'instrument" },
                        { icon: Award, title: "Référence accréditation", desc: "Numéro d'accréditation du laboratoire" },
                        { icon: FileCheck, title: "Conditions d'essai", desc: "Température, humidité, conditions de mesure" },
                      ].map((item, i) => (
                        <div key={i} className="flex items-start gap-3">
                          <div className="bg-[#635BFF]/10 p-2 rounded-lg">
                            <item.icon className="w-5 h-5 text-[#635BFF]" />
                          </div>
                          <div>
                            <h4 className="font-semibold text-[#0A2540]">{item.title}</h4>
                            <p className="text-sm text-gray-600">{item.desc}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div className="bg-green-50 border border-green-200 rounded-xl p-4 flex items-start gap-3">
                    <CheckCircle2 className="w-6 h-6 text-green-500 flex-shrink-0 mt-0.5" />
                    <div>
                      <h4 className="font-bold text-green-800">Conseil</h4>
                      <p className="text-green-700 text-sm">
                        Conservez ces certificats : ils constituent votre preuve de conformité en cas d'audit ou de litige, 
                        et documentent l'historique métrologique de chaque capteur.
                      </p>
                    </div>
                  </div>
                </section>

                {/* Section 7 */}
                <section id="section-7" className="mb-16">
                  <h2 className="text-3xl font-bold text-[#0A2540] mb-6 flex items-center gap-3">
                    <span className="bg-[#635BFF] text-white w-10 h-10 rounded-full flex items-center justify-center text-lg">7</span>
                    Bien choisir son prestataire d'étalonnage
                  </h2>

                  <p className="text-gray-700 mb-6">
                    Tous les prestataires ne se valent pas. Les critères à vérifier :
                  </p>

                  <div className="space-y-4 mb-8">
                    {[
                      { 
                        title: "Accréditation ISO/IEC 17025", 
                        desc: "Vérifiez son accréditation et la traçabilité de ses étalons de référence.",
                        important: true
                      },
                      { 
                        title: "Couverture complète du parc", 
                        desc: "Peut-il étalonner pression, couple, profondeur, inclinaison, débit ?" 
                      },
                      { 
                        title: "Délais d'intervention", 
                        desc: "Un capteur immobilisé, c'est un chantier retardé." 
                      },
                      { 
                        title: "Connaissance du matériel géotechnique", 
                        desc: "Enregistreurs de paramètres, capteurs de forage et d'injection (Jean Lutz, etc.)." 
                      },
                    ].map((item, i) => (
                      <div key={i} className={`bg-white rounded-xl border p-4 ${item.important ? 'border-[#635BFF] border-2' : ''}`}>
                        <div className="flex items-start gap-3">
                          <CheckCircle2 className={`w-5 h-5 flex-shrink-0 mt-0.5 ${item.important ? 'text-[#635BFF]' : 'text-green-500'}`} />
                          <div>
                            <h4 className="font-semibold text-[#0A2540]">{item.title}</h4>
                            <p className="text-sm text-gray-600">{item.desc}</p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>

                  <div className="bg-[#00D4FF]/10 border border-[#00D4FF]/30 rounded-lg p-4 flex items-start gap-2">
                    <Lightbulb className="w-5 h-5 text-[#00D4FF] flex-shrink-0 mt-0.5" />
                    <p className="text-sm text-[#0A2540]">
                      <strong>Le point souvent décisif :</strong> un prestataire qui connaît <strong>spécifiquement l'instrumentation géotechnique</strong> 
                      saura étalonner vos capteurs dans les conditions réelles de leur usage, ce qu'un laboratoire généraliste ne fait pas toujours.
                    </p>
                  </div>
                </section>

                {/* CTA Section */}
                <section className="bg-gradient-to-br from-[#635BFF] to-[#8B5CF6] rounded-2xl p-8 text-white mb-16">
                  <h2 className="text-2xl font-bold mb-4">L'étalonnage de votre instrumentation géotechnique</h2>
                  <p className="mb-6 text-white/90">
                    Un enregistreur de paramètres et ses capteurs ne valent que par la fiabilité de leurs mesures — et cette fiabilité 
                    passe par un étalonnage rigoureux, tracé au SI, réalisé par un prestataire qui connaît votre matériel.
                  </p>
                  <div className="bg-white/10 rounded-xl p-4 mb-6">
                    <p className="font-semibold">SIM Métrologie</p>
                    <p className="text-white/90 text-sm">
                      Partenaire de GEOTS, spécialisée dans la <strong>location, l'étalonnage et la maintenance de l'instrumentation géotechnique</strong> 
                      (enregistreurs de paramètres, capteurs de forage et d'injection, notamment Jean Lutz). SIM assure le suivi métrologique 
                      de votre parc, la traçabilité de vos mesures et la disponibilité d'un matériel toujours à jour.
                    </p>
                  </div>
                  <div className="flex flex-wrap gap-4">
                    <Link to="/annuaire/etalonnage-instrumentation-geotechnique" className="bg-white text-[#635BFF] px-6 py-3 rounded-lg font-semibold hover:bg-gray-100 transition-colors flex items-center gap-2">
                      Voir la fiche SIM Métrologie
                      <ArrowRight className="w-5 h-5" />
                    </Link>
                    <Link to="/materiel/demande?type=instrumentation&objet=etalonnage" className="bg-white/20 text-white px-6 py-3 rounded-lg font-semibold hover:bg-white/30 transition-colors">
                      Demander un devis
                    </Link>
                  </div>
                </section>

                {/* FAQ Section */}
                <section className="mb-16">
                  <h2 className="text-3xl font-bold text-[#0A2540] mb-6 flex items-center gap-3">
                    <HelpCircle className="w-8 h-8 text-[#635BFF]" />
                    Questions fréquentes
                  </h2>

                  <div className="space-y-3">
                    {faqs.map((faq, i) => (
                      <div key={i} className="bg-white rounded-xl border overflow-hidden">
                        <button
                          onClick={() => setOpenFaq(openFaq === i ? null : i)}
                          className="w-full px-6 py-4 flex items-center justify-between text-left hover:bg-gray-50 transition-colors"
                        >
                          <span className="font-semibold text-[#0A2540]">{faq.question}</span>
                          {openFaq === i ? (
                            <ChevronUp className="w-5 h-5 text-gray-400" />
                          ) : (
                            <ChevronDown className="w-5 h-5 text-gray-400" />
                          )}
                        </button>
                        {openFaq === i && (
                          <div className="px-6 pb-4">
                            <p className="text-gray-700">{faq.answer}</p>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </section>

                {/* Related Guides */}
                <section className="mb-8">
                  <h2 className="text-2xl font-bold text-[#0A2540] mb-6">Pour aller plus loin</h2>
                  <div className="grid md:grid-cols-3 gap-4">
                    <Link 
                      to="/guides/enregistreur-parametres-forage"
                      className="bg-white rounded-xl border p-4 hover:shadow-md transition-shadow"
                    >
                      <p className="font-medium text-[#0A2540] mb-2">Enregistreur de paramètres de forage</p>
                      <span className="text-sm text-[#635BFF] flex items-center gap-1">
                        Lire le guide
                        <ArrowRight className="w-4 h-4" />
                      </span>
                    </Link>
                    <Link 
                      to="/guides/materiel-de-forage"
                      className="bg-white rounded-xl border p-4 hover:shadow-md transition-shadow"
                    >
                      <p className="font-medium text-[#0A2540] mb-2">Matériel de forage : le guide complet</p>
                      <span className="text-sm text-[#635BFF] flex items-center gap-1">
                        Lire le guide
                        <ArrowRight className="w-4 h-4" />
                      </span>
                    </Link>
                    <Link 
                      to="/guides/choisir-pressiometre-manuel-automatique"
                      className="bg-white rounded-xl border p-4 hover:shadow-md transition-shadow"
                    >
                      <p className="font-medium text-[#0A2540] mb-2">Choisir son pressiomètre</p>
                      <span className="text-sm text-[#635BFF] flex items-center gap-1">
                        Lire le guide
                        <ArrowRight className="w-4 h-4" />
                      </span>
                    </Link>
                  </div>
                </section>

                {/* Footer note */}
                <div className="text-center text-gray-500 text-sm border-t pt-8">
                  <p>
                    Ce guide est fourni à titre informatif. Les obligations réglementaires et normatives applicables à votre activité 
                    doivent être vérifiées au regard de votre situation et des référentiels en vigueur (NF EN ISO/IEC 17025:2017, exigences des marchés).
                  </p>
                  <p className="mt-2">Publié le 15 mars 2026</p>
                </div>
              </article>
            </main>
          </div>
        </div>
      </div>
    </>
  );
};

export default EtalonnageCapteurs;
